import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useGlobalLocation } from '@/contexts/LocationContext';
import { formatDateNL } from '@/lib/format';
import { Briefcase, Plus, Loader2, Inbox, Pencil, Archive, Trash2, Link2, Users, Download, Save, UserPlus } from 'lucide-react';

const STATUS_LABEL = {
  concept: 'Concept', open: 'Open', gesloten: 'Gesloten',
  vervuld: 'Vervuld', gearchiveerd: 'Gearchiveerd',
};
const STATUS_BADGE = {
  concept: 'bg-slate-100 text-slate-700',
  open: 'bg-green-100 text-green-800',
  gesloten: 'bg-amber-100 text-amber-800',
  vervuld: 'bg-indigo-100 text-indigo-700',
  gearchiveerd: 'bg-slate-200 text-slate-500',
};
const TYPE_LABEL = { vast: 'Vast', tijdelijk: 'Tijdelijk', oproep: 'Oproep' };
const STATUS_OPTIONS = [
  ['concept', 'Concept'], ['open', 'Open'], ['gesloten', 'Gesloten'],
  ['vervuld', 'Vervuld'], ['gearchiveerd', 'Gearchiveerd'],
];

export default function Vacatures() {
  const { user } = useAuth();
  const { globalLocation, setGlobalLocation } = useGlobalLocation();
  const scoped = globalLocation && globalLocation !== 'all' ? globalLocation : null;
  const [vacatures, setVacatures] = useState([]);
  const [locations, setLocations] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState({ type: '', msg: '' });
  const [editing, setEditing] = useState(null); // vacature obj or 'new'
  const [busyId, setBusyId] = useState(null);
  const [viewingApplicants, setViewingApplicants] = useState(null); // vacature obj
  const [appCounts, setAppCounts] = useState({});

  const canEdit = ['owner', 'admin', 'multi_location', 'single_location'].includes(user?.role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (scoped) params.location_slug = scoped;
      if (statusFilter) params.status = statusFilter;
      const appParams = {};
      if (scoped) appParams.location = scoped;
      const [{ data }, locResp, appResp] = await Promise.all([
        api.get('/vacatures', { params }),
        api.get('/locations'),
        api.get('/applications', { params: appParams }),
      ]);
      setVacatures(data.vacatures || []);
      setLocations(locResp.data || []);
      const counts = {};
      (appResp.data.applications || []).forEach((a) => {
        counts[a.vacature_id] = (counts[a.vacature_id] || 0) + 1;
      });
      setAppCounts(counts);
    } catch {
      setFeedback({ type: 'error', msg: 'Kon vacatures niet laden' });
    } finally {
      setLoading(false);
    }
  }, [scoped, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const locName = useMemo(() => {
    const m = {};
    locations.forEach((l) => { m[l.slug] = l.name; });
    return m;
  }, [locations]);

  const archive = async (v) => {
    setBusyId(v.id);
    try {
      await api.put(`/vacatures/${v.id}`, { status: 'gearchiveerd' }, { headers: { 'If-Match': `"${v.id}-${v.version}"` } });
      setFeedback({ type: 'success', msg: 'Vacature gearchiveerd' });
      await load();
    } catch (e) {
      const s = e.response?.status;
      setFeedback({ type: 'error', msg: s === 409 || s === 412 ? 'Vernieuwd: vacature is gewijzigd' : 'Archiveren mislukt' });
      await load();
    } finally { setBusyId(null); }
  };

  const copyLink = async (v) => {
    const url = `${window.location.origin}/werken-bij/${v.id}`;
    try {
      await navigator.clipboard.writeText(url);
      setFeedback({ type: 'success', msg: `Publieke link gekopieerd: ${url}` });
    } catch {
      window.prompt('Kopieer de publieke link:', url);
    }
  };

  const remove = async (v) => {
    if (!window.confirm(`Vacature "${v.title}" definitief verwijderen?`)) return;
    setBusyId(v.id);
    try {
      await api.delete(`/vacatures/${v.id}`, { headers: { 'If-Match': `"${v.id}-${v.version}"` } });
      setFeedback({ type: 'success', msg: 'Vacature verwijderd' });
      await load();
    } catch (e) {
      const s = e.response?.status;
      setFeedback({ type: 'error', msg: s === 409 || s === 412 ? 'Vernieuwd: vacature is gewijzigd' : 'Verwijderen mislukt' });
      await load();
    } finally { setBusyId(null); }
  };

  return (
    <div className="space-y-6 max-w-6xl" data-testid="page-vacatures">
      <div className="flex items-center justify-between border-b border-[#e4e9f0] pb-4">
        <div>
          <h2 className="text-3xl font-bold text-tw1-brand-navy flex items-center gap-2">
            <Briefcase size={26} /> Vacatures
          </h2>
          <p className="mt-1 text-sm text-tw1-text-muted">
            Beheer openstaande vacatures per locatie{scoped ? ` — ${locName[scoped] || scoped}` : ' — alle locaties waartoe je toegang hebt'}.
          </p>
        </div>
        {canEdit && (
          <button onClick={() => setEditing('new')} data-testid="btn-create-vacature"
            className="flex items-center gap-1.5 rounded-md bg-tw1-brand-navy px-3 py-2 text-sm font-medium text-white hover:bg-brand-navy-light">
            <Plus size={15} /> Nieuwe vacature
          </button>
        )}
      </div>

      {feedback.msg && (
        <div className={`rounded-md px-3 py-2 text-sm ${feedback.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`} data-testid="vacature-feedback">
          {feedback.msg}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white p-3">
        <select value={globalLocation} onChange={(e) => setGlobalLocation(e.target.value)} data-testid="vacature-filter-location"
          className="rounded-md border border-slate-300 px-3 py-1.5 text-sm">
          <option value="all">Alle locaties</option>
          {locations.map((l) => <option key={l.slug} value={l.slug}>{l.name}</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="vacature-filter-status"
          className="rounded-md border border-slate-300 px-3 py-1.5 text-sm">
          <option value="">Alle statussen</option>
          {STATUS_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center gap-2 p-6 text-sm text-slate-500"><Loader2 size={14} className="animate-spin" /> Laden…</div>
        ) : vacatures.length === 0 ? (
          <div className="p-12 text-center" data-testid="vacature-empty">
            <Inbox size={36} className="mx-auto mb-2 text-slate-300" />
            <p className="text-sm text-slate-500">Geen vacatures gevonden</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                  <th className="py-2 px-3">Titel</th>
                  <th className="py-2 px-3">Locatie</th>
                  <th className="py-2 px-3">Functie</th>
                  <th className="py-2 px-3">Type</th>
                  <th className="py-2 px-3 text-right">Uren</th>
                  <th className="py-2 px-3">Periode</th>
                  <th className="py-2 px-3">Status</th>
                  <th className="py-2 px-3 text-center">Sollicitaties</th>
                  {canEdit && <th className="py-2 px-3 text-right">Acties</th>}
                </tr>
              </thead>
              <tbody>
                {vacatures.map((v) => (
                  <tr key={v.id} data-testid={`vacature-row-${v.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2 px-3 font-medium text-slate-900">{v.title}</td>
                    <td className="py-2 px-3 text-slate-600">{locName[v.location_slug] || v.location_slug}</td>
                    <td className="py-2 px-3 text-slate-700">{v.functie || '—'}</td>
                    <td className="py-2 px-3 text-slate-700">{TYPE_LABEL[v.employment_type] || '—'}</td>
                    <td className="py-2 px-3 text-right tabular-nums">{v.hours != null ? `${v.hours}u` : '—'}</td>
                    <td className="py-2 px-3 tabular-nums text-slate-600 text-xs">
                      {v.open_date ? formatDateNL(v.open_date) : '—'}{v.close_date ? ` → ${formatDateNL(v.close_date)}` : ''}
                    </td>
                    <td className="py-2 px-3">
                      <span className={`rounded px-2 py-0.5 text-xs ${STATUS_BADGE[v.status] || 'bg-slate-100'}`} data-testid={`vacature-status-${v.id}`}>
                        {STATUS_LABEL[v.status] || v.status}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-center">
                      <button onClick={() => setViewingApplicants(v)} data-testid={`btn-applicants-${v.id}`}
                        className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100">
                        <Users size={12} /> {appCounts[v.id] || 0}
                      </button>
                    </td>
                    {canEdit && (
                      <td className="py-2 px-3 text-right whitespace-nowrap">
                        {v.status === 'open' && (
                          <button onClick={() => copyLink(v)} disabled={busyId === v.id} data-testid={`btn-share-${v.id}`}
                            className="mr-1 inline-flex items-center gap-1 rounded-md border border-sky-300 px-2 py-1 text-xs font-medium text-sky-700 hover:bg-sky-50 disabled:opacity-60">
                            <Link2 size={12} /> Deel link
                          </button>
                        )}
                        <button onClick={() => setEditing(v)} disabled={busyId === v.id} data-testid={`btn-edit-${v.id}`}
                          className="mr-1 inline-flex items-center gap-1 rounded-md border border-slate-300 px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-60">
                          <Pencil size={12} /> Bewerken
                        </button>
                        {v.status !== 'gearchiveerd' && (
                          <button onClick={() => archive(v)} disabled={busyId === v.id} data-testid={`btn-archive-${v.id}`}
                            className="mr-1 inline-flex items-center gap-1 rounded-md border border-amber-300 px-2 py-1 text-xs font-medium text-amber-700 hover:bg-amber-50 disabled:opacity-60">
                            <Archive size={12} /> Archiveren
                          </button>
                        )}
                        <button onClick={() => remove(v)} disabled={busyId === v.id} data-testid={`btn-delete-${v.id}`}
                          className="inline-flex items-center gap-1 rounded-md border border-rose-300 px-2 py-1 text-xs font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-60">
                          <Trash2 size={12} /> Verwijderen
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <VacatureDialog
          vacature={editing === 'new' ? null : editing}
          locations={locations}
          defaultSlug={scoped}
          onClose={(reload) => { setEditing(null); if (reload) load(); }}
        />
      )}
      {viewingApplicants && (
        <ApplicantsModal
          vacature={viewingApplicants}
          canEdit={canEdit}
          onClose={(reload) => { setViewingApplicants(null); if (reload) load(); }}
        />
      )}
    </div>
  );
}

function VacatureDialog({ vacature, locations, defaultSlug, onClose }) {
  const isEdit = !!vacature;
  const [form, setForm] = useState({
    location_slug: vacature?.location_slug || defaultSlug || locations[0]?.slug || '',
    title: vacature?.title || '',
    description: vacature?.description || '',
    functie: vacature?.functie || '',
    employment_type: vacature?.employment_type || '',
    hours: vacature?.hours ?? '',
    status: vacature?.status || 'concept',
    open_date: vacature?.open_date || '',
    close_date: vacature?.close_date || '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    const payload = {
      title: form.title,
      description: form.description || null,
      functie: form.functie || null,
      employment_type: form.employment_type || null,
      hours: form.hours === '' ? null : parseFloat(form.hours),
      status: form.status,
      open_date: form.open_date || null,
      close_date: form.close_date || null,
    };
    try {
      if (isEdit) {
        await api.put(`/vacatures/${vacature.id}`, payload, { headers: { 'If-Match': `"${vacature.id}-${vacature.version}"` } });
      } else {
        await api.post('/vacatures', { ...payload, location_slug: form.location_slug });
      }
      onClose(true);
    } catch (err) {
      const s = err.response?.status;
      if (s === 409 || s === 412) setError('Vernieuw de pagina, vacature is door iemand anders gewijzigd');
      else setError(err.response?.data?.detail || 'Opslaan mislukt');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <form onSubmit={handleSubmit} className="w-full max-w-xl rounded-xl bg-white p-6 shadow-2xl space-y-3 max-h-[90vh] overflow-y-auto" data-testid="vacature-form">
        <h3 className="text-lg font-semibold text-tw1-brand-navy">{isEdit ? 'Vacature bewerken' : 'Nieuwe vacature'}</h3>
        <div className="grid grid-cols-2 gap-3">
          <SelectField label="Locatie" value={form.location_slug} disabled={isEdit}
            onChange={(v) => setForm({ ...form, location_slug: v })}
            options={locations.map((l) => [l.slug, l.name])} testid="vacature-location" />
          <SelectField label="Status" value={form.status} onChange={(v) => setForm({ ...form, status: v })}
            options={STATUS_OPTIONS} testid="vacature-status-select" />
        </div>
        <Field label="Titel" required value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="vacature-title" />
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Omschrijving</label>
          <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="vacature-description"
            rows={4} className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm" />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Functie" value={form.functie} onChange={(v) => setForm({ ...form, functie: v })} testid="vacature-functie" />
          <SelectField label="Dienstverband" value={form.employment_type} onChange={(v) => setForm({ ...form, employment_type: v })}
            options={[['', '—'], ['vast', 'Vast'], ['tijdelijk', 'Tijdelijk'], ['oproep', 'Oproep']]} testid="vacature-type" />
          <Field label="Uren/week" type="number" value={form.hours} onChange={(v) => setForm({ ...form, hours: v })} testid="vacature-hours" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Open vanaf" type="date" value={form.open_date} onChange={(v) => setForm({ ...form, open_date: v })} testid="vacature-open-date" />
          <Field label="Sluit op" type="date" value={form.close_date} onChange={(v) => setForm({ ...form, close_date: v })} testid="vacature-close-date" />
        </div>

        {error && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="vacature-form-error">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={() => onClose(false)} className="rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50">
            Annuleren
          </button>
          <button type="submit" disabled={submitting} data-testid="btn-submit-vacature"
            className="rounded-md bg-tw1-brand-navy px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-navy-light disabled:opacity-60">
            {submitting ? 'Bezig…' : (isEdit ? 'Opslaan' : 'Aanmaken')}
          </button>
        </div>
      </form>
    </div>
  );
}

const APP_STATUS_LABEL = {
  ontvangen: 'Ontvangen', in_behandeling: 'In behandeling', uitgenodigd: 'Uitgenodigd',
  afgewezen: 'Afgewezen', aangenomen: 'Aangenomen',
};
const APP_STATUS_BADGE = {
  ontvangen: 'bg-slate-100 text-slate-700',
  in_behandeling: 'bg-blue-100 text-blue-700',
  uitgenodigd: 'bg-indigo-100 text-indigo-700',
  afgewezen: 'bg-rose-100 text-rose-700',
  aangenomen: 'bg-green-100 text-green-800',
};
const APP_STATUS_OPTIONS = ['ontvangen', 'in_behandeling', 'uitgenodigd', 'afgewezen', 'aangenomen'];

function ApplicantsModal({ vacature, canEdit, onClose }) {
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [err, setErr] = useState('');
  const [notes, setNotes] = useState({});
  const [changed, setChanged] = useState(false);
  const [hiring, setHiring] = useState(null); // application obj

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/applications', { params: { vacature_id: vacature.id } });
      setApps(data.applications || []);
      const n = {};
      (data.applications || []).forEach((a) => { n[a.id] = a.notitie || ''; });
      setNotes(n);
    } catch {
      setErr('Kon sollicitaties niet laden');
    } finally {
      setLoading(false);
    }
  }, [vacature.id]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (a, status) => {
    setBusyId(a.id);
    setErr('');
    try {
      await api.put(`/applications/${a.id}/status`, { status }, { headers: { 'If-Match': `"${a.id}-${a.version}"` } });
      setChanged(true);
      await load();
    } catch (e) {
      const s = e.response?.status;
      if (s === 409) setErr(e.response?.data?.detail || 'Statusovergang niet toegestaan of gewijzigd');
      else setErr('Status bijwerken mislukt');
      await load();
    } finally { setBusyId(null); }
  };

  const saveNote = async (a) => {
    setBusyId(a.id);
    setErr('');
    try {
      await api.put(`/applications/${a.id}/note`, { notitie: notes[a.id] }, { headers: { 'If-Match': `"${a.id}-${a.version}"` } });
      await load();
    } catch (e) {
      const s = e.response?.status;
      setErr(s === 409 ? 'Vernieuwd: sollicitatie is gewijzigd' : 'Notitie opslaan mislukt');
      await load();
    } finally { setBusyId(null); }
  };

  const downloadCv = async (a) => {
    setBusyId(a.id);
    try {
      const resp = await api.get(`/applications/${a.id}/cv`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = a.cv_filename || 'cv';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setErr('CV downloaden mislukt');
    } finally { setBusyId(null); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-3xl rounded-xl bg-white p-6 shadow-2xl max-h-[90vh] overflow-y-auto" data-testid="applicants-modal">
        <div className="flex items-center justify-between border-b border-slate-200 pb-3 mb-4">
          <h3 className="text-lg font-semibold text-tw1-brand-navy flex items-center gap-2">
            <Users size={18} /> Sollicitaties — {vacature.title}
          </h3>
          <button onClick={() => onClose(changed)} className="rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50" data-testid="applicants-close">
            Sluiten
          </button>
        </div>

        {err && <div className="mb-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="applicants-error">{err}</div>}

        {loading ? (
          <div className="flex items-center gap-2 p-6 text-sm text-slate-500"><Loader2 size={14} className="animate-spin" /> Laden…</div>
        ) : apps.length === 0 ? (
          <div className="p-10 text-center" data-testid="applicants-empty">
            <Inbox size={32} className="mx-auto mb-2 text-slate-300" />
            <p className="text-sm text-slate-500">Nog geen sollicitaties voor deze vacature</p>
          </div>
        ) : (
          <div className="space-y-4">
            {apps.map((a) => (
              <div key={a.id} className="rounded-lg border border-slate-200 p-4" data-testid={`applicant-${a.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold text-slate-900">{a.name}</p>
                    <p className="text-xs text-slate-600">{a.email}{a.telefoon ? ` · ${a.telefoon}` : ''}{a.woonplaats ? ` · ${a.woonplaats}` : ''}</p>
                  </div>
                  <span className={`rounded px-2 py-0.5 text-xs ${APP_STATUS_BADGE[a.status] || 'bg-slate-100'}`} data-testid={`applicant-status-${a.id}`}>
                    {APP_STATUS_LABEL[a.status] || a.status}
                  </span>
                </div>
                {a.motivatie && <p className="mt-2 whitespace-pre-line text-sm text-slate-700">{a.motivatie}</p>}
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {a.cv_exists && (
                    <button onClick={() => downloadCv(a)} disabled={busyId === a.id} data-testid={`btn-cv-${a.id}`}
                      className="inline-flex items-center gap-1 rounded-md border border-sky-300 px-2.5 py-1 text-xs font-medium text-sky-700 hover:bg-sky-50 disabled:opacity-60">
                      <Download size={12} /> CV downloaden
                    </button>
                  )}
                  {canEdit && (
                    <select value={a.status} onChange={(e) => setStatus(a, e.target.value)} disabled={busyId === a.id}
                      data-testid={`applicant-status-select-${a.id}`}
                      className="rounded-md border border-slate-300 px-2 py-1 text-xs">
                      {APP_STATUS_OPTIONS.map((s) => <option key={s} value={s}>{APP_STATUS_LABEL[s]}</option>)}
                    </select>
                  )}
                  {canEdit && (
                    a.employee_id ? (
                      <span className="inline-flex items-center gap-1 rounded-md bg-green-50 px-2.5 py-1 text-xs font-medium text-green-700" data-testid={`applicant-hired-${a.id}`}>
                        <UserPlus size={12} /> Aangenomen
                      </span>
                    ) : (
                      <button onClick={() => setHiring(a)} disabled={busyId === a.id} data-testid={`btn-hire-${a.id}`}
                        className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-60">
                        <UserPlus size={12} /> Aannemen
                      </button>
                    )
                  )}
                </div>
                {canEdit && (
                  <div className="mt-3 flex items-end gap-2">
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-slate-600 mb-1">Interne notitie</label>
                      <textarea rows={2} value={notes[a.id] || ''} onChange={(e) => setNotes({ ...notes, [a.id]: e.target.value })}
                        data-testid={`applicant-note-${a.id}`} className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm" />
                    </div>
                    <button onClick={() => saveNote(a)} disabled={busyId === a.id} data-testid={`btn-save-note-${a.id}`}
                      className="inline-flex items-center gap-1 rounded-md bg-tw1-brand-navy px-2.5 py-1.5 text-xs font-medium text-white hover:bg-brand-navy-light disabled:opacity-60">
                      <Save size={12} /> Opslaan
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      {hiring && (
        <HireForm
          application={hiring}
          vacature={vacature}
          onClose={(result) => {
            setHiring(null);
            if (result?.employee_id) {
              setChanged(true);
              navigate(`/contracten?employee_id=${result.employee_id}`);
            }
          }}
        />
      )}
    </div>
  );
}

function HireForm({ application, vacature, onClose }) {
  const [form, setForm] = useState({
    personeelsnummer: '', home_location_id: vacature.location_id || '',
    contract_type: 'vast', contract_hours: vacature.hours ?? 0,
    datum_in_dienst: new Date().toISOString().slice(0, 10),
    adres: '', huisnummer: '', postcode: '', geboortedatum: '', bsn: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    api.get('/applications/next-personeelsnummer')
      .then(({ data }) => { if (!cancelled) setForm((f) => ({ ...f, personeelsnummer: data.suggested })); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const payload = {
        personeelsnummer: form.personeelsnummer,
        home_location_id: form.home_location_id || null,
        contract_type: form.contract_type,
        contract_hours: parseFloat(form.contract_hours) || 0,
        datum_in_dienst: form.datum_in_dienst || null,
        adres: form.adres || null, huisnummer: form.huisnummer || null,
        postcode: form.postcode || null, geboortedatum: form.geboortedatum || null,
        bsn: form.bsn || null,
      };
      const { data } = await api.post(`/applications/${application.id}/hire`, payload);
      onClose(data);
    } catch (err) {
      const s = err.response?.status;
      if (s === 409) setError(err.response?.data?.detail || 'Personeelsnummer al in gebruik of al aangenomen');
      else if (s === 403) setError('Geen toegang om op deze locatie aan te nemen');
      else setError(err.response?.data?.detail || 'Aannemen mislukt');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <form onSubmit={handleSubmit} className="w-full max-w-lg rounded-xl bg-white p-6 shadow-2xl space-y-3 max-h-[90vh] overflow-y-auto" data-testid="hire-form">
        <h3 className="text-lg font-semibold text-tw1-brand-navy flex items-center gap-2">
          <UserPlus size={18} /> Aannemen — {application.name}
        </h3>
        <p className="text-xs text-slate-500">NAW van de kandidaat wordt overgenomen. Vul de dienstverband-gegevens aan; daarna opent de contract-wizard voorgevuld.</p>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Naam (kandidaat)" value={application.name} onChange={() => {}} testid="hire-name" />
          <Field label="E-mail (kandidaat)" value={application.email || ''} onChange={() => {}} testid="hire-email" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Personeelsnummer" required value={form.personeelsnummer} onChange={(v) => setForm({ ...form, personeelsnummer: v })} testid="hire-pnr" />
          <Field label="Datum in dienst" type="date" value={form.datum_in_dienst} onChange={(v) => setForm({ ...form, datum_in_dienst: v })} testid="hire-startdate" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <SelectField label="Contracttype" value={form.contract_type} onChange={(v) => setForm({ ...form, contract_type: v })}
            options={[['vast', 'Vast'], ['tijdelijk', 'Tijdelijk'], ['oproep', 'Oproep']]} testid="hire-contract" />
          <Field label="Uren/week" type="number" value={form.contract_hours} onChange={(v) => setForm({ ...form, contract_hours: v })} testid="hire-hours" />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Adres" value={form.adres} onChange={(v) => setForm({ ...form, adres: v })} testid="hire-adres" />
          <Field label="Huisnr." value={form.huisnummer} onChange={(v) => setForm({ ...form, huisnummer: v })} testid="hire-huisnummer" />
          <Field label="Postcode" value={form.postcode} onChange={(v) => setForm({ ...form, postcode: v })} testid="hire-postcode" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Woonplaats (kandidaat)" value={application.woonplaats || ''} onChange={() => {}} testid="hire-woonplaats" />
          <Field label="Geboortedatum" type="date" value={form.geboortedatum} onChange={(v) => setForm({ ...form, geboortedatum: v })} testid="hire-geboortedatum" />
        </div>
        <Field label="BSN (alleen owner)" value={form.bsn} onChange={(v) => setForm({ ...form, bsn: v })} testid="hire-bsn" />

        {error && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="hire-error">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={() => onClose(null)} className="rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50">
            Annuleren
          </button>
          <button type="submit" disabled={submitting} data-testid="btn-hire-submit"
            className="rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-60">
            {submitting ? 'Bezig…' : 'Aannemen & contract opstellen'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', required, testid }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      <input type={type} required={required} value={value ?? ''} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm" />
    </div>
  );
}

function SelectField({ label, value, onChange, options, testid, disabled }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} disabled={disabled}
        className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm disabled:bg-slate-100 disabled:text-slate-500">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}
